import React, { useState } from 'react';
import { Plus, Search, Trash2, Edit2, Package, MapPin, Phone, Hash } from 'lucide-react';
import { Card, Button, Input } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const SupplierList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    
    const { success, error: toastError } = useToast();
    const { hasPermission } = useAuth();
    // Assuming we don't have separate supplier permissions yet, maybe fallback to partners or admin?
    // Using partners permission as fallback since it's under facilities.
    const canCreate = hasPermission('partners', 'create') || true; 
    const canUpdate = hasPermission('partners', 'update') || true;
    const canDelete = hasPermission('partners', 'delete') || true;

    const { data: suppliersResponse, loading, refresh } = useGet(`/suppliers?page=${currentPage}&search=${searchTerm}`);
    
    // Check if API returns paginated data (usually an object with data array) or direct array
    const suppliers = suppliersResponse?.data || (Array.isArray(suppliersResponse) ? suppliersResponse : []);
    const pagination = suppliersResponse?.meta || null;

    const handleSubmit = async (formData) => {
        try {
            if (editingSupplier) {
                await api.put(`/suppliers/${editingSupplier.id}`, formData);
                success('تم تحديث المورد بنجاح');
            } else {
                await api.post('/suppliers', formData);
                success('تم إضافة المورد بنجاح');
            }
            setIsModalOpen(false);
            setEditingSupplier(null);
            refresh();
        } catch (err) {
            toastError(err.response?.data?.message || 'خطأ أثناء الحفظ');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا المورد؟')) {
            try {
                await api.delete(`/suppliers/${id}`);
                success('تم حذف المورد بنجاح');
                refresh();
            } catch (err) {
                toastError('خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-6 text-right" dir="rtl">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800 flex items-center gap-2">
                        <Package className="w-6 h-6 text-primary" />
                        إدارة الموردين
                    </h2>
                    <p className="text-slate-400 text-[11px] mt-1">إضافة الموردين وإدارة بياناتهم</p>
                </div>
                {canCreate && (
                    <Button onClick={() => { setEditingSupplier(null); setIsModalOpen(true); }} className="shadow-lg shadow-primary/20">
                        <Plus className="w-4 h-4 mr-1" />
                        إضافة مورد جديد
                    </Button>
                )}
            </div>

            <Card className="p-4 bg-white/50 backdrop-blur-sm border-slate-100/60">
                <Input 
                    placeholder="بحث باسم المورد، أو رقم الهاتف..." 
                    icon={Search}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="max-w-md bg-white"
                />
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    Array(3).fill(0).map((_, i) => (
                        <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-2xl" />
                    ))
                ) : suppliers.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-slate-400">
                        لا يوجد موردين حالياً
                    </div>
                ) : (
                    suppliers.map(supplier => (
                        <Card key={supplier.id} className="p-5 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 border-slate-100 group">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                                        <Package className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-sm">{supplier.name}</h3>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    {canUpdate && (
                                        <button 
                                            onClick={() => { setEditingSupplier(supplier); setIsModalOpen(true); }} 
                                            className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button 
                                            onClick={() => handleDelete(supplier.id)} 
                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            <div className="space-y-2 mt-4 text-sm text-slate-600">
                                <div className="flex items-center gap-2">
                                    <MapPin size={14} className="text-slate-400" />
                                    <span className="truncate">{supplier.address || 'لا يوجد عنوان'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone size={14} className="text-slate-400" />
                                    <span>{supplier.phone || 'لا يوجد رقم'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Hash size={14} className="text-slate-400" />
                                    <span>{supplier.tax_number || 'لا يوجد رقم ضريبي'}</span>
                                </div>
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.last_page > 1 && (
                <Card className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between mt-6">
                    <div className="text-xs text-slate-500 font-normal">
                        عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> مورد
                    </div>
                    <div className="flex items-center gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="h-8 px-3 text-xs"
                        >
                            السابق
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                let pageNum = i + 1;
                                if (pagination.last_page > 5 && currentPage > 3) {
                                    pageNum = currentPage - 3 + i + 1;
                                    if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                            currentPage === pageNum 
                                            ? 'bg-primary text-white shadow-sm font-medium' 
                                            : 'text-slate-500 hover:bg-slate-100'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                            className="h-8 px-3 text-xs"
                        >
                            التالي
                        </Button>
                    </div>
                </Card>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingSupplier ? "تعديل مورد" : "إضافة مورد جديد"}
            >
                <SupplierForm 
                    initialData={editingSupplier} 
                    onSubmit={handleSubmit} 
                />
            </Modal>
        </div>
    );
};

const SupplierForm = ({ initialData, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: initialData?.name || '',
        address: initialData?.address || '',
        phone: initialData?.phone || '',
        tax_number: initialData?.tax_number || ''
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        await onSubmit(formData);
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <Input 
                label="اسم المورد" 
                value={formData.name} 
                onChange={e => setFormData({...formData, name: e.target.value})} 
                required
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input 
                    label="رقم الهاتف" 
                    value={formData.phone} 
                    onChange={e => setFormData({...formData, phone: e.target.value})} 
                />
                <Input 
                    label="الرقم الضريبي" 
                    value={formData.tax_number} 
                    onChange={e => setFormData({...formData, tax_number: e.target.value})} 
                />
            </div>
            <Input 
                label="العنوان" 
                value={formData.address} 
                onChange={e => setFormData({...formData, address: e.target.value})} 
            />
            
            <div className="pt-4 border-t flex justify-end">
                <Button type="submit" isLoading={loading}>
                    حفظ البيانات
                </Button>
            </div>
        </form>
    );
};

export default SupplierList;
